import { Trans, useTranslation } from 'react-i18next';
import { Turnstile } from '@marsidev/react-turnstile';
import { useMediaQuery, useTheme } from '@mui/material';

import { useLogin } from '~/components/organisms/auth/hooks/use-login';
import { BUTTON_TYPE } from '~/components/ui/button';
import { DividerText } from '~/components/ui/divider/divider-text';
import { Icon } from '~/components/ui/icon';
import { Link } from '~/components/ui/link';
import { Text } from '~/components/ui/text';
import { CLOUDFLARE_KEY, REDIRECT_URI, TABLET_MIN_WIDTH } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import { COLORS } from '~/styles/colors';

import {
  AuthButton,
  LoginSocialGoogleStyled,
  StyledActionsWrapper,
  StyledDividerTextWrapper,
  StyledFormBodyWrapper,
  StyledInput,
  StyledInputWrapper,
} from './styles/formStyled';

type SignInFormProps = {
  className?: string;
  cameFromPopup?: boolean;
  selectedQR?: number;
  openConfirmationPopup?: () => void;
  qrCode?: any;
  isAnonCreateQr?: boolean;
};
export default function SignInForm({
  className,
  cameFromPopup,
  selectedQR,
  isAnonCreateQr,
  openConfirmationPopup,
  qrCode,
}: SignInFormProps) {
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(TABLET_MIN_WIDTH));

  const {
    setTurnstileToken,
    setGoogleToken,
    isEmailValid,
    login,
    handleEmailValue,
    email,
    turnstileToken,
    setIsError,
  } = useLogin({
    cameFromPopup,
    selectedQR,
    isAnonCreateQr,
    openConfirmationPopup,
    qrCode,
  });

  return (
    <StyledFormBodyWrapper className={className}>
      {!isMobile && (
        <>
          <LoginSocialGoogleStyled
            isOnlyGetToken
            client_id={process.env.GOOGLE_CLIENT_ID}
            redirect_uri={REDIRECT_URI}
            onResolve={({ data }) => setGoogleToken(data)}
            onReject={() => setIsError(t('alerts:socialError'))}
            scope={'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'}
          >
            <AuthButton type={BUTTON_TYPE.outlined} borderColor={COLORS.grey200} color={COLORS.textMain}>
              <Icon withParentColor={false} size={28} className="google-icon" icon="GoogleIcon" />
              {t('signIn:buttons.signInWithGoogle')}
            </AuthButton>
          </LoginSocialGoogleStyled>
          <StyledDividerTextWrapper>
            <DividerText>{t('signIn:dividerText')}</DividerText>
          </StyledDividerTextWrapper>
        </>
      )}
      <StyledInputWrapper>
        <StyledInput
          icon={<Icon className="icon-mail" icon="Mail" />}
          fullWidth
          placeholder={t('signIn:inputs.email.placeholder')}
          value={email}
          onChange={handleEmailValue}
          type="email"
          helperText={isEmailValid ? t('errors:emailIsValid') : undefined}
          error={isEmailValid}
        />
      </StyledInputWrapper>
      <Turnstile
        siteKey={CLOUDFLARE_KEY}
        options={{
          theme: 'light',
        }}
        action="login"
        onSuccess={setTurnstileToken}
      />

      <StyledActionsWrapper>
        <AuthButton fullWidth onClick={login} disabled={isEmailValid || !turnstileToken || !email}>
          {t('signIn:buttons.login')}
        </AuthButton>
      </StyledActionsWrapper>

      <Text variant="t16Regular" sx={{ textAlign: 'center' }}>
        <Trans
          i18nKey="signIn:registerLink"
          components={{
            1: <Link to={PATH.SIGN_UP} />,
          }}
        />
      </Text>

      {isMobile && (
        <>
          <StyledDividerTextWrapper>
            <DividerText>{t('signIn:loginGoogle')}</DividerText>
          </StyledDividerTextWrapper>
          <LoginSocialGoogleStyled
            isOnlyGetToken
            client_id={process.env.GOOGLE_CLIENT_ID}
            redirect_uri={REDIRECT_URI}
            onResolve={({ data }) => setGoogleToken(data)}
            onReject={() => setIsError(t('alerts:socialError'))}
            scope={'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'}
          >
            <AuthButton type={BUTTON_TYPE.outlined} borderColor={COLORS.grey200} color={COLORS.textMain}>
              <Icon withParentColor={false} size={28} className="google-icon" icon="GoogleIcon" />
              {t('signIn:buttons.signInWithGoogle')}
            </AuthButton>
          </LoginSocialGoogleStyled>
        </>
      )}
    </StyledFormBodyWrapper>
  );
}
